import React, { ReactNode, useMemo } from "react";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { currencyFormatter } from "@common/helpers/currency";
import { sumMonetaryValues } from "@common/helpers/monetary";
import { BankCashItemType } from "@common/types/bankCashItem";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { FinancialAccount } from "@common/types/financialAccount";
import { counterpartyCellFn } from "@components/importer/counterpartyCellFn";
import { CellType } from "@components/spreadsheet/cells";
import { FinancialAccountCell } from "@components/spreadsheet/cells/FinancialAccountCell";
import { ColumnDef, SpreadsheetData } from "@components/spreadsheet/types";
import { TransactionSplitClassCell } from "@components/transactions/TransactionSplitV2/TransactionSplitClassCell";
import { TransactionSplitLedgerCell } from "@components/transactions/TransactionSplitV2/TransactionSplitLedgerCell";
import { isCheckingAccount } from "@helpers/financialAccount";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { cashItemTypeLabel, cashItemTypeOf } from "./cashItemAmount";
import { InvalidCellsByRowId } from "./cashItemImportValidation";
import styles from "./CashItemImporter.module.scss";

interface UseCashItemImporterColumnsParams {
    counterpartiesMap: Map<string, string>;
    financialAccountsById: ReadonlyMap<number, FinancialAccount>;
    invalidCellsByRowId: InvalidCellsByRowId;
}

interface CreateLedgerColumnParams {
    field: "accountId" | "accrualAccountId";
    label: ReactNode;
    ledgerBasis: AccountingLedgerBasis;
    entityIdOf: (row: SpreadsheetData[number]) => number | undefined;
}

const RequiredLabel = ({ children }: { children: ReactNode }) => (
    <>
        {children}
        <span role="img" aria-label="required" className={styles.requiredMark}>
            *
        </span>
    </>
);

function bankAccountFilterForRow(
    row: SpreadsheetData[number],
): ((account: FinancialAccount) => boolean) | undefined {
    const amount = typeof row.amount === "number" ? row.amount : null;
    if (cashItemTypeOf(amount) !== BankCashItemType.CHECK) {
        return undefined;
    }
    return isCheckingAccount;
}

function createLedgerColumn({
    field,
    label,
    ledgerBasis,
    entityIdOf,
}: CreateLedgerColumnParams): ColumnDef {
    return {
        field,
        label,
        cellType: CellType.LEDGER,
        width: 180,
        cellFn: (props) => (
            <TransactionSplitLedgerCell
                {...props}
                value={typeof props.value === "string" ? props.value : null}
                entityId={entityIdOf(props.row)}
                ledgerBasis={ledgerBasis}
            />
        ),
    };
}

export function useCashItemImporterColumns({
    counterpartiesMap,
    financialAccountsById,
    invalidCellsByRowId,
}: UseCashItemImporterColumnsParams): ColumnDef[] {
    const { enabled: isGlFirst } = useIsGlFirst();
    const { hasAccess: hasClassesAccess } = useAllowFeature(
        PREMIUM_FEATURES.CLASSES,
    );

    return useMemo(() => {
        const entityIdOf = (row: SpreadsheetData[number]) =>
            typeof row.financialAccountId === "number"
                ? (financialAccountsById.get(row.financialAccountId)
                      ?.entityId ?? undefined)
                : undefined;

        const categoryColumns: ColumnDef[] = isGlFirst
            ? []
            : [
                  {
                      field: "categoryId",
                      label: <RequiredLabel>Category</RequiredLabel>,
                      cellType: CellType.CATEGORY,
                      width: 200,
                  },
              ];

        const classColumns: ColumnDef[] = hasClassesAccess
            ? [
                  {
                      field: "classIds",
                      label: "Class",
                      cellType: CellType.CLASS,
                      width: 140,
                      cellFn: (props) => (
                          <TransactionSplitClassCell
                              {...props}
                              value={
                                  Array.isArray(props.value)
                                      ? props.value
                                      : null
                              }
                          />
                      ),
                  },
              ]
            : [];

        const dataColumns: ColumnDef[] = [
            {
                field: "_type",
                label: "Type",
                cellType: CellType.STRING,
                width: 110,
                aggregation: () => "Total",
                isEditable: false,
                className: styles.readOnlyCell,
                cellFn: ({ row }) =>
                    cashItemTypeLabel(
                        typeof row.amount === "number" ? row.amount : null,
                    ) || null,
                toClipboard: (_value, row) =>
                    cashItemTypeLabel(
                        typeof row.amount === "number" ? row.amount : null,
                    ),
            },
            {
                field: "checkNumber",
                label: "Reference number",
                cellType: CellType.STRING,
                width: 140,
            },
            {
                field: "date",
                label: <RequiredLabel>Date</RequiredLabel>,
                cellType: CellType.DATE,
                width: 120,
            },
            {
                field: "financialAccountId",
                label: <RequiredLabel>Bank account</RequiredLabel>,
                cellType: CellType.FINANCIAL_ACCOUNT,
                financialAccounts: [...financialAccountsById.values()],
                width: 200,
                cellFn: (props) => (
                    <FinancialAccountCell
                        {...props}
                        value={
                            typeof props.value === "number" ? props.value : null
                        }
                        accountFilterPredicate={bankAccountFilterForRow(
                            props.row,
                        )}
                    />
                ),
            },
            {
                field: "counterpartyId",
                label: <RequiredLabel>Counterparty</RequiredLabel>,
                cellType: CellType.COUNTERPARTY,
                counterpartiesMap,
                width: 180,
                cellFn: counterpartyCellFn(counterpartiesMap),
            },
            {
                field: "memo",
                label: "Memo",
                cellType: CellType.STRING,
                width: 180,
            },
            ...categoryColumns,
            createLedgerColumn({
                field: "accountId",
                label: <RequiredLabel>Cash ledger</RequiredLabel>,
                ledgerBasis: AccountingLedgerBasis.cash,
                entityIdOf,
            }),
            createLedgerColumn({
                field: "accrualAccountId",
                label: "Accrual ledger",
                ledgerBasis: AccountingLedgerBasis.accruals,
                entityIdOf,
            }),
            ...classColumns,
            {
                field: "amount",
                label: <RequiredLabel>Amount</RequiredLabel>,
                cellType: CellType.NUMBER,
                isMonetary: true,
                allowNegative: true,
                width: 130,
                aggregation: (data) =>
                    currencyFormatter.format(
                        sumMonetaryValues(
                            data.map((row) =>
                                typeof row.amount === "number" ? row.amount : 0,
                            ),
                        ),
                    ),
            },
        ];

        const rowNumberColumn: ColumnDef = {
            field: "_rowNumber",
            label: "",
            cellType: CellType.ROW_NUMBER,
            width: 40,
            pinned: "left",
            isEditable: false,
        };

        return [
            rowNumberColumn,
            ...dataColumns.map((column): ColumnDef => ({
                ...column,
                isValid: ({ rowData }) =>
                    !invalidCellsByRowId
                        .get(String(rowData._id))
                        ?.has(column.field),
            })),
        ];
    }, [
        counterpartiesMap,
        financialAccountsById,
        hasClassesAccess,
        invalidCellsByRowId,
        isGlFirst,
    ]);
}
